import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, Mail } from "lucide-react";
import { useLanguage } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Post-article validation poll — now aligned with the "test inicial" pivot.
 *
 * Flow:
 *   1. Pick an option (what blocks you most) → reveals email capture inline
 *   2. Submit email → POST /api/waitlist with the poll answer attached
 *   3. Success state: reinforces "you'll receive the initial test by email"
 *
 * Copy is punchier and the block is visually louder than before (accent border,
 * more padding, larger heading). Consent is required (RGPD).
 */
const OPTIONS = [
  { key: "start",       label: { ca: "Començar",           es: "Empezar" } },
  { key: "consistency", label: { ca: "Ser constant",       es: "Ser constante" } },
  { key: "pain",        label: { ca: "El dolor",           es: "El dolor" } },
  { key: "shame",       label: { ca: "La vergonya",        es: "La vergüenza" } },
  { key: "other",       label: { ca: "Una altra cosa",     es: "Otra cosa" } },
];

export default function ArticleValidationPoll({ articleId, lang = "ca" }) {
  const { t } = useLanguage();
  const [selected, setSelected] = useState(null);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const eyebrow = lang === "es" ? "Nos ayudas a construir esto" : "Ens ajudes a construir això";
  const heading = lang === "es"
    ? "¿Qué es lo que más te cuesta hoy?"
    : "Què és el que més et costa avui?";
  const sub = lang === "es"
    ? "Elige una opción — nos dice mucho más de lo que crees. Después te enviaremos un test inicial para entender mejor tu punto de partida."
    : "Escull una opció — ens diu molt més del que et penses. Després t'enviarem un test inicial per entendre millor el teu punt de partida.";
  const emailHeading = lang === "es"
    ? "Perfecto. ¿Dónde te enviamos el test inicial?"
    : "Perfecte. On t'enviem el test inicial?";
  const submitLabel = lang === "es" ? "Recibir el test inicial" : "Rebre el test inicial";
  const microcopy = lang === "es"
    ? "Sin spam, sin presión, sin compromiso. Solo cuando tengamos algo útil para ti."
    : "Sense spam, sense pressió, sense compromís. Només quan tinguem alguna cosa útil per a tu.";
  const successHeading = lang === "es" ? "Recibido" : "Rebut";
  const successBody = lang === "es"
    ? "Gracias por darnos una pista. Pronto te llegará un test inicial al correo para entender mejor tu punto de partida. Sin prisa, sin compromisos."
    : "Gràcies per donar-nos una pista. Aviat t'arribarà un test inicial al correu per entendre millor el teu punt de partida. Sense pressa, sense compromisos.";

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t("form.errors.invalidEmail"));
      return;
    }
    if (!consent) {
      toast.error(t("form.errors.consentRequired"));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, {
        email,
        source: `article:${articleId}`,
        validation_answer: selected,
        validation_article_id: articleId,
        language: lang,
        consent_accepted: true,
      });
      setDone(true);
      toast.success(successHeading);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("form.errors.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Thank-you state ───
  if (done) {
    return (
      <section
        data-testid="validation-poll-success"
        className="mt-16 rounded-2xl border-l-4 border-[#B4C5A4] bg-white p-8 sm:p-10 shadow-[0_4px_30px_-12px_rgba(11,37,69,0.15)]"
      >
        <div className="flex items-center gap-2.5 mb-3">
          <div className="h-7 w-7 rounded-full bg-[#0B2545] flex items-center justify-center">
            <Check className="h-4 w-4 text-[#F9F8F6]" strokeWidth={3} />
          </div>
          <p className="font-sans-display text-xs tracking-[0.22em] uppercase text-[#0B2545] font-semibold">
            {successHeading}
          </p>
        </div>
        <p className="font-sans-display text-xl text-[#0F172A] leading-snug mb-2">
          {successBody}
        </p>
      </section>
    );
  }

  return (
    <section
      data-testid="validation-poll"
      className="mt-16 rounded-2xl border-l-4 border-[#B4C5A4] bg-white p-7 sm:p-10 shadow-[0_4px_30px_-12px_rgba(11,37,69,0.12)]"
      aria-labelledby="poll-heading"
    >
      <p className="text-[10px] uppercase tracking-[0.24em] text-[#B4C5A4] font-sans-display font-semibold mb-3">
        {eyebrow}
      </p>
      <h2
        id="poll-heading"
        className="font-sans-display text-2xl sm:text-3xl text-[#0B2545] tracking-tight leading-snug mb-3"
      >
        {heading}
      </h2>
      <p className="text-[#475569] leading-relaxed mb-6 max-w-2xl">{sub}</p>

      {/* Options grid — larger buttons, more clickable */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-6" data-testid="poll-options">
        {OPTIONS.map((opt) => {
          const isSelected = selected === opt.key;
          return (
            <button
              type="button"
              key={opt.key}
              onClick={() => setSelected(opt.key)}
              data-testid={`poll-option-${opt.key}`}
              aria-pressed={isSelected}
              className={`h-14 px-3 text-sm font-sans-display rounded-lg border-2 transition-all ${
                isSelected
                  ? "border-[#0B2545] bg-[#0B2545] text-[#F9F8F6] shadow-md scale-[1.02]"
                  : "border-slate-200 bg-white text-[#475569] hover:border-[#0B2545]/50 hover:text-[#0B2545] hover:bg-[#F9F8F6]"
              }`}
            >
              {opt.label[lang]}
            </button>
          );
        })}
      </div>

      {/* Email capture — revealed after selection */}
      {selected && (
        <form
          onSubmit={submit}
          className="space-y-4 pt-6 border-t-2 border-dashed border-slate-200 animate-in fade-in slide-in-from-bottom-2 duration-300"
          data-testid="poll-email-form"
        >
          <div className="flex items-center gap-2 text-[#0B2545]">
            <Mail className="h-4 w-4" />
            <p className="font-sans-display font-medium tracking-tight text-base">
              {emailHeading}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2.5">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("form.hero.emailPlaceholder")}
              data-testid="poll-email-input"
              className="flex-1 h-12 px-4 bg-white border-2 border-slate-200 rounded-md text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0B2545]/20 focus:border-[#0B2545] transition"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="poll-submit-button"
              className="lift-btn h-12 px-6 bg-[#0B2545] text-[#F9F8F6] font-sans-display font-medium tracking-wide rounded-md hover:bg-[#0a1f3a] disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {submitting ? t("form.full.submitFallback") : submitLabel}
            </button>
          </div>

          <div className="flex items-start gap-3 pt-1">
            <input
              id={`poll-consent-${articleId}`}
              type="checkbox"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              data-testid="poll-consent-checkbox"
              className="mt-1 h-4 w-4 accent-[#0B2545] cursor-pointer flex-shrink-0"
            />
            <label htmlFor={`poll-consent-${articleId}`} className="text-xs text-[#475569] leading-relaxed cursor-pointer">
              {t("form.consent.preLink")}{" "}
              <Link
                to="/politica-privacitat"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#0B2545] underline underline-offset-2 hover:text-[#0a1f3a]"
              >
                {t("form.consent.link")}
              </Link>
              {t("form.consent.postLink")}
            </label>
          </div>

          <p className="text-xs text-slate-400 italic pt-1">{microcopy}</p>
        </form>
      )}
    </section>
  );
}
